import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO, { createBreadcrumbSchema, createFAQSchema, softwareApplicationSchema } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, CheckCircle, Zap, Image, Sparkles, Camera, Palette, Clock, Star, Heart } from 'lucide-react';

const AIImageGeneratorDepop = () => {
  const faqs = [
    {
      question: "What image style works best for Depop?",
      answer: "Depop favours authentic, aesthetically pleasing photos. Our AI creates stylish flat lays, on-model looks, and trendy backgrounds that match Depop's curated aesthetic while helping your items stand out."
    },
    {
      question: "Can I create on-model product photos without a model?",
      answer: "Yes! Our AI can generate realistic on-model shots, styled flat lays, and creative product presentations. Perfect for clothing, accessories, and vintage pieces popular on Depop."
    },
    {
      question: "Are AI-generated images allowed on Depop?",
      answer: "Yes, AI-generated images are permitted for product photography on Depop. Our platform provides full commercial rights, making images safe for your Depop shop."
    },
    {
      question: "How can I match Depop's aesthetic with AI images?",
      answer: "Use prompts that emphasise trendy backgrounds, natural lighting, and authentic styling. Our AI understands aesthetic trends popular with Gen Z and Millennial Depop shoppers."
    }
  ];

  const features = [
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "Depop Aesthetic",
      description: "Generate trendy, Instagram-worthy images that match Depop's curated visual style"
    },
    {
      icon: <Camera className="w-6 h-6" />,
      title: "Flat Lay Magic",
      description: "Create beautiful flat lay compositions with stylish backgrounds and props"
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: "On-Model Looks",
      description: "Generate realistic on-model shots without needing actual model photography"
    },
    {
      icon: <Palette className="w-6 h-6" />,
      title: "Trend-Forward Styling",
      description: "Stay on-trend with aesthetic backgrounds and styling popular with Depop buyers"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Bulk Creation",
      description: "List faster by generating multiple unique images for your entire inventory"
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Save Hours Weekly",
      description: "Skip lengthy photo shoots and editing. Create scroll-stopping images instantly"
    }
  ];

  const useCases = [
    "Vintage clothing sellers curating aesthetic shops",
    "Streetwear and sneaker resellers",
    "Y2K and 90s fashion enthusiasts",
    "Handmade jewellery and accessory sellers",
    "Thrifted and sustainable fashion sellers",
    "Designer resale and luxury consignment",
    "Indie brand owners launching collections",
    "Student sellers building side hustles"
  ];

  return (
    <>
      <SEO
        title="AI Image Generator for Depop | Aesthetic Listing Photos"
        description="Create aesthetic Depop listing photos with AI. Trendy flat lays, on-model shots, and stylish backgrounds for UK Depop sellers. Stand out in the feed. Used by 4,000+ Depop shops."
        keywords="ai image generator depop, depop listing photos, depop aesthetic images, depop photography ai, depop product photos, depop flat lay, depop on model, depop seller tools uk"
        url="/ai-image-generator-depop"
        jsonLd={[
          softwareApplicationSchema,
          createBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'AI Image Generator for Depop', url: '/ai-image-generator-depop' }
          ]),
          createFAQSchema(faqs)
        ]}
      />
      
      <Header />
      
      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 via-pink-500/5 to-background" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-4 bg-gradient-to-r from-red-500/20 to-pink-500/20 text-foreground border-0">
                Trusted by 4,000+ UK Depop Sellers
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                AI Image Generator for Depop
                <span className="block bg-gradient-to-r from-red-400 to-pink-400 bg-clip-text text-transparent drop-shadow-sm">
                  Aesthetic Listing Photos
                </span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Create scroll-stopping Depop listings with AI. Trendy flat lays, on-model shots, 
                and aesthetic backgrounds that match Depop's curated vibe. Stand out in the feed.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-gradient-to-r from-red-500 to-pink-500 hover:opacity-90 text-white">
                  <Link to="/try-generator">
                    Create Depop Images Free <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to="/gallery">View Aesthetic Examples</Link>
                </Button>
              </div>
              
              {/* Trust Badges */}
              <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  <span>4.9/5 from UK sellers</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Depop Aesthetic</span>
                </div>
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-pink-500" />
                  <span>Instant Creation</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Why UK Depop Sellers Love Our AI Generator
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {features.map((feature, index) => (
                <Card key={index} className="border border-transparent bg-gradient-to-br from-red-500/5 to-pink-500/5 hover:from-red-500/10 hover:to-pink-500/10 hover:border-red-500/20 transition-all shadow-sm hover:shadow-md">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-red-500/20 to-pink-500/20 flex items-center justify-center text-foreground mb-4">
                      {feature.icon}
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                Perfect For Every Depop Seller
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {useCases.map((useCase, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 rounded-lg bg-gradient-to-r from-red-500/5 to-pink-500/5">
                    <CheckCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                    <span>{useCase}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Cross-links */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-8">
              Selling on Other UK Platforms?
            </h2>
            <div className="flex flex-wrap justify-center gap-4 max-w-3xl mx-auto">
              <Button asChild variant="outline">
                <Link to="/ai-image-generator-ebay-uk">AI for eBay UK</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/ai-image-generator-amazon-uk">AI for Amazon UK</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/ai-image-generator-for-etsy">AI for Etsy UK</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Frequently Asked Questions
            </h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <Card key={index} className="border-0 bg-gradient-to-br from-red-500/5 to-pink-500/5">
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">
                Start Creating Aesthetic Depop Listings Today
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                Join 4,000+ UK Depop sellers using AI to create scroll-stopping listings. 
                No photography skills or expensive equipment needed.
              </p>
              <Button asChild size="lg" className="bg-gradient-to-r from-red-500 to-pink-500 hover:opacity-90 text-white">
                <Link to="/try-generator">
                  Generate Depop Images Free <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default AIImageGeneratorDepop;
